import type { ToolResultType } from "schemas/chat";
import type {
  ChatMessage,
  ChatMessageContent,
} from "schemas/generated/chatMessage";

import React, { useCallback, useEffect, useRef, useState } from "react";

import { Chat } from "@editor/components/editor/ai/chat/Chat";
import ChatHeader from "@editor/components/editor/ai/chat/ChatHeader";
import useChatMessageLimits from "@editor/components/editor/ai/chat/hooks/useChatMessageLimits";
import ManageChats from "@editor/components/editor/ai/chat/ManageChats";
import AssetComponent from "@editor/components/editor/ai/chat/MessageInput/assetNodeExtension";
import ProductComponent from "@editor/components/editor/ai/chat/MessageInput/productNodeExtension";
import { useCurrentChatContext } from "@editor/contexts/CurrentChatContext";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useLocalStorage } from "@editor/hooks/useLocalStorage";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { chatRouter } from "@editor/trpc/chat";
import { marketingClient } from "@editor/trpc/marketing";
import { triggerRouter } from "@editor/trpc/trigger";
import { workspaceUtils } from "@editor/trpc/workspace";

import { useActiveTriggerJobsForChat } from "@/features/react-editor/components/chat/store/ChatStoreContext";
import { InlineSidebar } from "@replo/design-system/components/inline-sidebar/InlineSidebar";
import { useIsMobile } from "@replo/design-system/components/shadcn/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import Placeholder from "@tiptap/extension-placeholder";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useLocation, useSearchParams } from "react-router-dom";
import { Markdown as TiptapMarkdown } from "tiptap-markdown";
import { v4 } from "uuid";

import { useChatSidebar } from "../../hooks/useChatSidebar";
import {
  useDefaultSelectedElementDesignation,
  useModifiersStore,
  useModifiersStoreActions,
} from "../modifiers/store/ModifiersStoreContext";
import HardBreakMarkdown from "./extensions/hardBreakMarkdown";
import {
  useChatStoreActions,
  useIsChatLoading,
  useMessagesForChat,
} from "./store/ChatStoreContext";
import {
  chatEditorHandleKeyDown,
  chatEditorHandlePaste,
  chatEditorOnUpdate,
  getCleanedChatPrompt,
} from "./utils/tiptapUtils";

// Consolidated in schemas/chat

export default function ReactEditorChat() {
  const location = useLocation();
  const projectId = useCurrentProjectId();
  const logEvent = useLogAnalytics();

  const [searchParams, setSearchParams] = useSearchParams();
  const pageId = searchParams.get("pageId");

  const {
    sendChatRequest,
    chatId,
    files,
    setFiles,
    chatError,
    forceScrollToBottomRef,
  } = useCurrentChatContext();

  const defaultDesignation = useDefaultSelectedElementDesignation();
  const { messagesRemaining, hasUnlimitedMessages } = useChatMessageLimits();

  const { open: isChatSidebarOpen, setOpen: setIsChatSidebarOpen } =
    useChatSidebar();
  const isMobile = useIsMobile();

  const { displayMode } = useModifiersStore();
  const modifierActions = useModifiersStoreActions();

  const isChatLoading = useIsChatLoading(chatId);
  const {
    getStreamingState,
    addOrUpdateMessageForChat,
    syncMessagesForChat,
    updateStreamingStatus,
    removeActiveTriggerJob,
  } = useChatStoreActions();

  const activeTriggerJobs = useActiveTriggerJobsForChat(chatId);

  const hasPopulatedMarketingSeedRef = useRef(false);

  const [atPosition, setAtPosition] = React.useState<number | null>(null);

  const handleToggleDisplayMode = () => {
    const isEnteringSelectMode = displayMode !== "select";
    modifierActions.toggleDisplayMode();
    if (isEnteringSelectMode) {
      setIsChatSidebarOpen(false);
    }
  };

  const [autoSubmitData, setAutoSubmitData] = useState<{
    prompt: string;
    shouldSubmit: boolean;
    isSubmitting?: boolean;
  } | null>(null);

  const storage = useLocalStorage();

  const allMessages = useMessagesForChat(chatId);

  const { mutate: cancelRun } = triggerRouter.cancelRun.useMutation({});

  const {
    data: paginatedMessagesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = chatRouter.getPaginatedMessages.useInfiniteQuery(
    chatId && projectId ? { chatId, projectId, limit: 20 } : skipToken,
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      staleTime: 0,
    },
  );

  const paginatedMessages = React.useMemo(() => {
    if (!paginatedMessagesData?.pages) {
      return [];
    }

    // Reverse pages so older pages (fetched later) come first
    const allMessages = paginatedMessagesData.pages
      .slice()
      .reverse()
      .flatMap((page) => page.messages);

    return allMessages;
  }, [paginatedMessagesData]);

  useEffect(() => {
    if (chatId && paginatedMessages.length > 0) {
      syncMessagesForChat(chatId, paginatedMessages);
    }
  }, [paginatedMessages, syncMessagesForChat, chatId]);

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();

    if (!chatEditor || isChatLoading) {
      return;
    }

    if (!hasUnlimitedMessages && messagesRemaining <= 0) {
      return;
    }
    const currentPrompt = getCleanedChatPrompt(chatEditor);
    chatEditor.commands.setContent("", false);
    setFiles(null);

    if (hasPopulatedMarketingSeedRef.current) {
      localStorage.removeItem("replo_marketing_seed");
      hasPopulatedMarketingSeedRef.current = false;
    }

    setIsChatSidebarOpen(false);

    if (displayMode === "select") {
      modifierActions.toggleDisplayMode();
    }
    await sendChatRequest({
      prompt: currentPrompt,
      isHidden: false,
      defaultDesignation,
      filesToUpload: files,
    });
  };

  const chatEditor = useEditor({
    extensions: [
      StarterKit.configure({
        hardBreak: false,
      }),
      AssetComponent,
      ProductComponent,
      HardBreakMarkdown,
      TiptapMarkdown.configure({
        /*
         * NOTE (Kevin 2025-09-24):
         * breaks=false because the HardBreak extension emits <br /> directly.
         * html=true keeps <chat-token> placeholders intact through markdown.
         * tightLists=true prevents blank paragraphs between list items.
         */
        breaks: false,
        html: true,
        tightLists: true,
      }),
      Placeholder.configure({
        placeholder: "Ask for any edits or customizations",
      }),
    ],
    content: "",
    onUpdate: chatEditorOnUpdate(setAtPosition, atPosition),
    editorProps: {
      attributes: {
        style: "font-size: 14px; white-space: pre-wrap;",
        class: "react-editor-chat",
      },
      handleKeyDown: chatEditorHandleKeyDown(
        setAtPosition,
        atPosition,
        handleSubmit,
      ),
      handlePaste: chatEditorHandlePaste(setFiles),
    },
  });

  const storedData = localStorage.getItem("replo_marketing_seed");
  if (storedData && chatEditor) {
    const marketingSeedData = JSON.parse(storedData);
    if (marketingSeedData?.prompt) {
      logEvent("marketing.site.prompt", {
        prompt: marketingSeedData.prompt,
        jti: marketingSeedData.jti,
        hasImage: Boolean(marketingSeedData.hasImage),
      });

      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("chatId", "");
        return newParams;
      });
      if (marketingSeedData?.jti && marketingSeedData?.hasImage) {
        (async () => {
          const { blob: base64Blob, contentType } =
            await marketingClient.getMarketingSeedImage.query({
              jti: marketingSeedData.jti,
            });
          const binaryString = atob(base64Blob);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: contentType });
          const file = new File([blob], "marketing-seed-image", {
            type: contentType,
          });
          setFiles([file]);

          setAutoSubmitData({
            prompt: marketingSeedData.prompt,
            shouldSubmit: true,
          });
        })();
      } else {
        setAutoSubmitData({
          prompt: marketingSeedData.prompt,
          shouldSubmit: true,
        });
      }

      hasPopulatedMarketingSeedRef.current = true;
      localStorage.removeItem("replo_marketing_seed");
    }
  }

  const handleStop = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (chatId) {
        const streamingState = getStreamingState(chatId);

        if (streamingState.controller) {
          streamingState.controller.abort();
        }

        if (activeTriggerJobs.length > 0) {
          for (const job of activeTriggerJobs) {
            void cancelRun({ runId: job.jobId });
            removeActiveTriggerJob(chatId, job.jobId);
          }
        }

        updateStreamingStatus(chatId, false);

        // NOTE (Kurt, 2025-08-12): We add a synthetic tool-result to the chat optimistically
        // to show the user the cancelled ChangeBlock. We do this because when the `handleStop`
        // function is invoked, it takes a non-deterministic amount of time to add the tool-result
        // to the `chat_messages` table. Instead of waiting for the tool-result or add a setTimeout,
        // we just add this message, which no longer exists once the latests chat message are fetched.
        const canceledContent = buildCanceledToolResultContent(allMessages);
        if (canceledContent) {
          addOrUpdateMessageForChat(chatId, v4(), canceledContent, "tool");
        }

        // Invalidate chat messages usage when user stops the chat early to ensure accurate counts
        void workspaceUtils.getChatMessagesUsage.invalidate();
      }
    },
    [
      chatId,
      getStreamingState,
      updateStreamingStatus,
      allMessages,
      addOrUpdateMessageForChat,
      activeTriggerJobs,
      removeActiveTriggerJob,
      cancelRun,
    ],
  );

  // Handle initial message from navigation state
  useEffect(() => {
    const initialData = location.state as {
      initialMessage?: string;
      initialFiles?: File[] | null;
    } | null;

    if (initialData?.initialMessage && chatEditor && !chatId) {
      if (initialData.initialFiles) {
        setFiles(initialData.initialFiles);
      }

      setAutoSubmitData({
        prompt: initialData.initialMessage,
        shouldSubmit: true,
      });

      window.history.replaceState(
        {},
        document.title,
        window.location.pathname + window.location.search,
      );
    }
  }, [location.state, chatEditor, chatId, setFiles]);

  // NOTE (Bobby, 2025-07-30): Have this as a separate useEffect since we need to wait for the files state to be set before submitting.
  // NOTE (Kevin, 2025-08-25): sendChatRequest is intentionally not included as a dependency because autoSubmit useEffect
  // had sendChatRequest as a dependency, which was being recreated on every render, causing an infinite loop.
  // We mark as submitting to prevent re-triggering while preserving data for retry on failure
  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to prevent infinite loops
  useEffect(() => {
    if (
      autoSubmitData?.shouldSubmit &&
      !autoSubmitData.isSubmitting &&
      chatEditor
    ) {
      const prompt = autoSubmitData.prompt;
      setAutoSubmitData({ prompt, shouldSubmit: true, isSubmitting: true });

      const handleAutoSubmit = async () => {
        try {
          await sendChatRequest({
            prompt,
            isHidden: false,
            defaultDesignation,
            filesToUpload: files,
          });
          chatEditor.commands.setContent("", false);
          setFiles(null);
          setAutoSubmitData(null);
        } catch {
          setAutoSubmitData({
            prompt,
            shouldSubmit: true,
            isSubmitting: false,
          });
        }
      };

      void handleAutoSubmit();
      setFiles(null);
    }
  }, [autoSubmitData, chatEditor]);

  // Find most recent chat for this page if needed
  const { data: mostRecentChatForPageData } =
    chatRouter.getPaginatedChats.useQuery(
      chatId === null && pageId && projectId
        ? { projectId, pageId, limit: 1 }
        : skipToken,
      {
        staleTime: 0,
      },
    );

  const mostRecentChatForPage = mostRecentChatForPageData?.chats[0];

  useEffect(() => {
    if (location.state?.initialMessage && !chatId) {
      return;
    }

    if (chatError && chatId && projectId && pageId) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("chatId", "");
        return newParams;
      });
    }
  }, [chatError, chatId, projectId, pageId, setSearchParams, location.state]);

  // NOTE (Kevin, 2025-08-21): Navigation logic for what happens when the user navigates to the chat page
  // from inputting a message or clicking on a page in Home or navigating to Chat via the sidebar.
  // P1: Never redirect when there's an initial message
  // P2: Restore last viewed chat from localStorage (when !pageId)
  // P3: Restore last chat for this page from database (when pageId but no chatId)
  // Also handles saving current chatId to localStorage
  useEffect(() => {
    if (location.state?.initialMessage && !chatId) {
      return;
    }

    // Restore last chat for this specific page from database
    if (
      chatId === null &&
      pageId &&
      projectId &&
      mostRecentChatForPage &&
      !location.state?.initialMessage
    ) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("chatId", mostRecentChatForPage.id);
        return newParams;
      });
      return;
    }

    // Restore last viewed chat from localStorage (when no specific page)
    if (
      chatId === null &&
      !pageId &&
      projectId &&
      !location.state?.initialMessage
    ) {
      const lastChatId = storage.getItem(
        `replo.editor.lastChatId.${projectId}`,
      );
      if (lastChatId) {
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("chatId", lastChatId);
          return newParams;
        });
        return;
      }
    }

    if (chatId && projectId) {
      storage.setItem(`replo.editor.lastChatId.${projectId}`, chatId);
    }
  }, [
    chatId,
    pageId,
    projectId,
    setSearchParams,
    location.state,
    storage,
    mostRecentChatForPage,
  ]);

  return (
    <div className="flex w-full h-full min-h-0 flex-row shadow-selectable border-0 sm:border-0.5 border-border sm:rounded-lg bg-white relative overflow-visible">
      <style>
        {`
          .react-editor-chat.tiptap p.is-editor-empty:first-child::before {
            color: var(--color-slate-500);
            content: attr(data-placeholder);
            float: left;
            height: 0;
            pointer-events: none;
          }
          .react-editor-chat.tiptap ol {
            list-style-type: decimal;
            list-style-position: outside;
            padding-left: 1.5rem;
          }
          .react-editor-chat.tiptap ul {
            list-style-type: disc;
            list-style-position: outside;
            padding-left: 1.5rem;
          }
        `}
      </style>

      {/* Left Chat List (desktop only; mobile handled by layout-level dock) */}
      {!isMobile && (
        <InlineSidebar
          open={isChatSidebarOpen}
          className={twMerge(
            "w-[250px] border-r-0.5 border-border rounded-tl-lg rounded-bl-lg",
            !isChatSidebarOpen && "border-r-transparent",
          )}
        >
          <div className="flex flex-col min-w-[250px] h-full">
            <ManageChats />
          </div>
        </InlineSidebar>
      )}

      {/* Main Chat Panel */}
      <div className="flex-1 flex flex-col h-full min-h-0 items-center min-w-[300px] overflow-hidden">
        <div className="hidden md:flex w-full justify-start p-4 pb-5 bg-white h-17 rounded-t-lg border-b-0.5 border-border">
          <ChatHeader />
        </div>
        <div className="p-3 pt-0 flex flex-col gap-4 h-full min-h-0 max-w-full sm:max-w-2xl lg:max-w-3xl w-full relative overflow-hidden pb-3">
          <Chat
            handleSubmit={handleSubmit}
            stop={handleStop}
            editor={chatEditor}
            fetchNextPage={() => void fetchNextPage()}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onForceScrollToTop={forceScrollToBottomRef}
            toggleDisplayMode={handleToggleDisplayMode}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * NOTE (Kurt, 2025-08-12): When the user presses Stop, this builds a synthetic canceled
 * 'tool-result' for the most recent in-flight assistant tool-call so the UI can close
 * the active card and we can optimistically show the user the cancelled ChangeBlock.
 *
 * We pick the last tool-call in the latest assistant message because one assistant
 * message can include multiple calls and the newest is the one still running.
 *
 * We must reuse the original toolCallId/toolName so rendering can link result→call;
 * otherwise the result is orphaned and the chat doesn't know where in the chat to put
 * this result.
 */
function buildCanceledToolResultContent(
  allMessages: ChatMessage[],
): ChatMessageContent | null {
  const lastAssistantWithToolCall = allMessages.findLast(
    (message) =>
      message.role === "assistant" &&
      message.content.some((contentItem) => contentItem.type === "tool-call"),
  );

  if (!lastAssistantWithToolCall) {
    return null;
  }

  const toolCalls = lastAssistantWithToolCall.content.filter(
    (contentItem) => contentItem.type === "tool-call",
  );

  const lastToolCall = toolCalls.findLast(() => true);
  if (!lastToolCall) {
    return null;
  }

  const toolName = lastToolCall.toolName as ToolResultType["toolName"];

  return [
    {
      type: "tool-result",
      toolCallId: lastToolCall.toolCallId,
      toolName,
      output: {
        type: "json",
        value: { success: false, isCanceled: true },
      },
    },
  ];
}
